import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { PartnerContactModal } from '@/components/travel/partners/partner-contact-modal';

export function TravelCariPartnerOperations({
  id,
  canEdit,
  commercial,
}: {
  id: string;
  canEdit: boolean;
  commercial: boolean;
}) {
  const { t } = useTranslation('partners');
  const [contactOpen, setContactOpen] = useState(false);
  const { data, isLoading, error, refetch } = trpc.partner.byId.useQuery(id);
  if (isLoading) return <p>{t('loadingPartner')}</p>;
  if (error || !data)
    return (
      <p role="alert" className="text-destructive">
        {t('errorLoading')}
      </p>
    );
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <section className="bg-card space-y-4 border p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">{t('contacts')}</h2>
          {canEdit && (
            <Button variant="outline" onClick={() => setContactOpen(true)}>
              {t('addContact')}
            </Button>
          )}
        </div>
        {data.contacts.length ? (
          data.contacts.map((contact) => (
            <div key={contact.id} className="border-t pt-3 text-sm">
              <p className="font-medium">
                {contact.name}
                {contact.isPrimary && ` · ${t('primary')}`}
              </p>
              <p className="text-muted-foreground">
                {[contact.role, contact.email, contact.phone].filter(Boolean).join(' · ')}
              </p>
            </div>
          ))
        ) : (
          <p className="text-muted-foreground text-sm">{t('noContacts')}</p>
        )}
      </section>
      {(commercial || data.contracts.length > 0) && (
        <section className="bg-card space-y-4 border p-5">
          <h2 className="font-semibold">{t('contractsLabel')}</h2>
          {data.contracts.length ? (
            data.contracts.map((contract) => (
              <div key={contract.id} className="border-t pt-3 text-sm">
                <p className="font-medium">{contract.title}</p>
                <p className="text-muted-foreground">
                  {new Date(contract.validFrom).toLocaleDateString()} –{' '}
                  {new Date(contract.validTo).toLocaleDateString()}
                </p>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">{t('noContracts')}</p>
          )}
        </section>
      )}
      {(commercial || data.services.length > 0) && (
        <section className="bg-card space-y-4 border p-5 lg:col-span-2">
          <h2 className="font-semibold">{t('activeServices')}</h2>
          {data.services.length ? (
            data.services.map((service) => (
              <div key={service.id} className="flex justify-between gap-4 border-t pt-3 text-sm">
                <div>
                  <p className="font-medium">{service.name}</p>
                  <p className="text-muted-foreground">{service.description}</p>
                </div>
                <span>
                  {new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: service.currency,
                  }).format(Number(service.basePrice))}
                </span>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">{t('noServices')}</p>
          )}
        </section>
      )}
      {contactOpen && (
        <PartnerContactModal
          open
          onOpenChange={setContactOpen}
          partnerId={id}
          onSuccess={() => {
            void refetch();
          }}
        />
      )}
    </div>
  );
}

export function TravelCariCustomerBookings({ id }: { id: string }) {
  const { t } = useTranslation('travelCari');
  const { data, isLoading, error } = trpc.customer.byId.useQuery(id);
  if (isLoading) return <p>{t('account.loading')}</p>;
  if (error || !data)
    return (
      <p role="alert" className="text-destructive">
        {t('account.loadFailed')}
      </p>
    );
  return (
    <section className="bg-card space-y-3 border p-5">
      <h2 className="font-semibold">{t('account.recentBookings')}</h2>
      {data.bookings.length ? (
        data.bookings.map((booking) => (
          <Link
            key={booking.id}
            to={`/travel/bookings/${booking.id}`}
            className="flex justify-between border-t pt-3 text-sm hover:underline"
          >
            <span>{booking.bookingReference}</span>
            <span>{booking.status}</span>
          </Link>
        ))
      ) : (
        <p className="text-muted-foreground text-sm">{t('account.noBookings')}</p>
      )}
    </section>
  );
}
